import { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import axios from "axios";
import { API } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Resiliente ai reload mobile: se la prima /auth/me fallisce per un blip di
    // rete (nessuna risposta o 5xx), ritenta prima di dichiarare l'utente
    // disconnesso. Un 401/403 esplicito, invece, significa "non autenticato" →
    // stop immediato (percorso veloce per gli utenti non loggati).
    const maxTries = 3;
    for (let attempt = 1; attempt <= maxTries; attempt++) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          setUser(null);
          setLoading(false);
          return;
        }
        if (attempt < maxTries) {
          await new Promise((r) => setTimeout(r, 600 * attempt));
          continue;
        }
        setUser(null);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Redirect helper kept for existing landing CTAs -> go to the login page
  const login = useCallback(() => {
    window.location.href = "/accedi";
  }, []);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name, birthDate, ageConfirmation) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      email, password, name,
      birth_date: birthDate,
      age_confirmation: ageConfirmation,
    });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const value = useMemo(
    () => ({ user, setUser, loading, login, loginWithPassword, register, logout, refreshUser }),
    [user, loading, login, loginWithPassword, register, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
